import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import {
    Button,
    Spinner,
    Modal,
    Form,
    Card,
    Badge,
    Dropdown,
    Row,
    Col
} from "react-bootstrap";
import {
    FiRefreshCw,
    FiSearch,
    FiX,
    FiChevronDown,
    FiTrendingUp,
    FiUsers,
    FiUserPlus,
    FiUserCheck,
    FiUserX,
    FiShield,
    FiEdit,
    FiToggleLeft,
    FiToggleRight,
} from "react-icons/fi";
import {
    FaUserShield,
    FaUserCog,
    FaUser,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment-timezone";
import "react-toastify/dist/ReactToastify.css";

const initialForm = {
    user_name: "",
    password: "",
    user_role: "editor",
    status: "active",
    can_access: 0,
};

const roleOptions = [
    { value: "viewer", label: "Viewer" },
    { value: "editor", label: "Editor" },
    { value: "supervisor", label: "Supervisor" },
    { value: "admin", label: "Admin" }
];

const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" }
];

const accessOptions = [
    { value: 1, label: "Enabled" },
    { value: 0, label: "Disabled" }
];

const Users = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [formData, setFormData] = useState(initialForm);
    const [editForm, setEditForm] = useState(initialForm);
    const [activeFilter, setActiveFilter] = useState(null);
    const [statsCollapsed, setStatsCollapsed] = useState(true);
    const fetchingRef = useRef(false);
    const userRole = localStorage.getItem("user_role");

    const fetchUsers = async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;

        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/admin/getallusers", {
                withCredentials: true
            });
            const filtered = res.data.filter(user => user.user_role !== "superadmin");
            setUsers(filtered);
            setFilteredUsers(filtered);
        } catch (err) {
            toast.error("Failed to fetch users");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const getSingaporeTimeUTC = () => {
        return moment().tz('Asia/Singapore').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                created_date: getSingaporeTimeUTC(),
                updated_date: getSingaporeTimeUTC(),
            };
            await axios.post("https://www.azzenterprises.com/api/admin/adduser", payload, {
                withCredentials: true
            });
            toast.success("User added successfully!");
            setShowModal(false);
            setFormData(initialForm);
            fetchUsers();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to add user";
            toast.error(message);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteUser) return;
        try {
            await axios.delete(`https://www.azzenterprises.com/api/admin/deleteuser/${deleteUser.id}`, {
                withCredentials: true
            });
            toast.success("User deleted successfully!");
            setShowDeleteModal(false);
            setDeleteUser(null);
            fetchUsers();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to delete user";
            toast.error(message);
        }
    };

    const handleUpdateUser = async (id, data) => {
        try {
            const payload = { ...data, updated_date: getSingaporeTimeUTC() };
            await axios.put(`https://www.azzenterprises.com/api/admin/updateuser/${id}`, payload, {
                withCredentials: true
            });

            setUsers((prev) =>
                prev.map((u) => (u.id === id ? { ...u, ...data } : u))
            );

            const editedFields = Object.keys(data).map((key) => {
                let value = data[key];
                switch (key) {
                    case "status": return `Status ${value} updated successfully`;
                    case "user_role": return `Role ${value} updated successfully`;
                    case "user_name": return `User Name ${value} updated successfully`;
                    case "can_access": return `Access ${value === 1 ? "to view all customers" : "to view only assigned customers"}`;
                    default: return `${key} (${value})`;
                }
            });

            toast.info(`${editedFields.join(", ")}`);
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to update user";
            toast.error(message);
        }
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setEditForm({
            user_name: user.user_name,
            user_role: user.user_role,
            status: user.status,
            can_access: user.can_access,
            password: ""
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!selectedUser) return;

        try {
            const updateData = {};
            if (editForm.user_name !== selectedUser.user_name) {
                updateData.user_name = editForm.user_name;
            }
            if (editForm.user_role !== selectedUser.user_role) {
                updateData.user_role = editForm.user_role;
            }
            if (editForm.status !== selectedUser.status) {
                updateData.status = editForm.status;
            }
            if (editForm.can_access !== selectedUser.can_access) {
                updateData.can_access = editForm.can_access;
            }
            if (editForm.password) {
                updateData.password = editForm.password;
            }

            if (Object.keys(updateData).length > 0) {
                await handleUpdateUser(selectedUser.id, updateData);
                setShowEditModal(false);
                setSelectedUser(null);
            } else {
                toast.info("No changes detected");
            }
        } catch (error) {
            toast.error("Failed to update user");
        }
    };

    const formatDate = (date) => {
        return moment(date)
            .tz("Asia/Singapore")
            .format("DD/MM/YYYY hh:mm:ss A");
    };

    const getRoleBadge = (role) => {
        const roleConfig = {
            superadmin: { bg: "danger", icon: <FaUserShield />, label: "Super Admin" },
            admin: { bg: "primary", icon: <FaUserCog />, label: "Admin" },
            supervisor: { bg: "info", icon: <FaUser />, label: "Supervisor" },
            editor: { bg: "warning", icon: <FaUser />, label: "Editor" },
            viewer: { bg: "secondary", icon: <FaUser />, label: "Viewer" },
            user: { bg: "dark", icon: <FaUser />, label: "User" }
        };

        const config = roleConfig[role] || roleConfig.user;
        return (
            <Badge bg={config.bg} className="d-flex align-items-center gap-1">
                {config.icon}
                {config.label}
            </Badge>
        );
    };

    const userStats = useMemo(() => {
        const stats = {
            totalUsers: users.length,
            activeUsers: 0,
            inactiveUsers: 0,
            roles: {},
            superadmins: 0,
            admins: 0,
            supervisors: 0,
            editors: 0,
            viewers: 0
        };

        users.forEach(user => {
            if (user.status === "active") {
                stats.activeUsers++;
            } else {
                stats.inactiveUsers++;
            }

            stats.roles[user.user_role] = (stats.roles[user.user_role] || 0) + 1;

            switch (user.user_role) {
                case "superadmin": stats.superadmins++; break;
                case "admin": stats.admins++; break;
                case "supervisor": stats.supervisors++; break;
                case "editor": stats.editors++; break;
                case "viewer": stats.viewers++; break;
                default: break;
            }
        });

        return stats;
    }, [users]);

    useEffect(() => {
        let filtered = users;

        if (searchTerm) {
            filtered = filtered.filter(user =>
                (user.user_name || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (activeFilter) {
            switch (activeFilter.type) {
                case 'status':
                    filtered = filtered.filter(user => user.status === activeFilter.value);
                    break;
                case 'role':
                    filtered = filtered.filter(user => user.user_role === activeFilter.value);
                    break;
                default:
                    break;
            }
        }

        setFilteredUsers(filtered);
    }, [users, searchTerm, activeFilter]);

    const clearFilters = () => {
        setSearchTerm("");
        setActiveFilter(null);
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            sortable: true,
            width: "60px"
        },
        {
            name: "User Name",
            selector: (row) => row.user_name,
            sortable: true,
            width: "180px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <FiEdit
                        size={14}
                        className="text-primary cursor-pointer"
                        onClick={() => openEditModal(row)}
                        style={{ cursor: 'pointer' }}
                    />
                    <span className="fw-medium">{row.user_name}</span>
                </div>
            )
        },
        {
            name: "Created",
            selector: (row) => formatDate(row.created_date),
            sortable: true,
            width: "200px"
        },
        {
            name: "Updated",
            minWidth: '200px',
            grow: 2,
            selector: (row) => formatDate(row.updated_date),
            sortable: true,
        },
        {
            name: "Role",
            selector: (row) => row.user_role,
            sortable: true,
            width: "150px",
            cell: (row) => {
                const currentUserRole = localStorage.getItem("user_role");
                const canEditRole = currentUserRole === "superadmin" || currentUserRole === "admin";

                if (!canEditRole) {
                    return getRoleBadge(row.user_role);
                }

                return (
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="link"
                            className="p-0 text-decoration-none"
                            style={{ color: 'inherit' }}
                        >
                            {getRoleBadge(row.user_role)}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {roleOptions.map((role) => (
                                <Dropdown.Item
                                    key={role.value}
                                    onClick={() => handleUpdateUser(row.id, { user_role: role.value })}
                                    active={row.user_role === role.value}
                                >
                                    {getRoleBadge(role.value)}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                );
            },
            right: true,
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            width: "120px",
            cell: (row) => {
                const isActive = row.status === "active";
                return (
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg={isActive ? "success" : "warning"}>
                            {isActive ? "Active" : "Inactive"}
                        </Badge>
                        <span
                            role="button"
                            onClick={() => handleUpdateUser(row.id, { status: isActive ? "inactive" : "active" })}
                            style={{ cursor: "pointer" }}
                        >
                            {isActive ?
                                <FiToggleRight className="text-success" size={20} /> :
                                <FiToggleLeft className="text-warning" size={20} />
                            }
                        </span>
                    </div>
                );
            },
            right: true,
        },
        {
            name: "Access",
            selector: (row) => row.can_access,
            sortable: true,
            width: "120px",
            cell: (row) => {
                const canAccess = row.can_access === 1;
                return (
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg={canAccess ? "success" : "danger"}>
                            {canAccess ? "Enabled" : "Disabled"}
                        </Badge>
                        <span
                            role="button"
                            onClick={() => handleUpdateUser(row.id, { can_access: canAccess ? 0 : 1 })}
                            style={{ cursor: "pointer" }}
                        >
                            {canAccess ?
                                <FiToggleRight className="text-success" size={20} /> :
                                <FiToggleLeft className="text-danger" size={20} />
                            }
                        </span>
                    </div>
                );
            },
            right: true,
        }
    ];

    const StatCard = ({ title, value, subtitle, icon, color, filterType, filterValue, isActive, onFilter }) => {
        const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        return (
            <Card
                className={`border-0 shadow-sm h-100 ${isActive ? 'active-stat-card' : ''}`}
                style={{
                    cursor: onFilter ? 'pointer' : 'default',
                    border: isActive ? `2px solid ${color}` : '1px solid #e5e7eb',
                    backgroundColor: isActive ? hexToRgba(color, 0.08) : '#ffffff',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    boxShadow: isActive ? `0 4px 12px ${hexToRgba(color, 0.15)}` : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                }}
                onClick={onFilter}
            >
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                                width: "48px",
                                height: "48px",
                                backgroundColor: isActive ? color : `${color}15`,
                                color: isActive ? '#fff' : color,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {React.cloneElement(icon, {
                                size: 20,
                                color: isActive ? '#fff' : color
                            })}
                        </div>
                        <div>
                            <h6 className="mb-1" style={{
                                fontSize: "0.875rem",
                                color: isActive ? color : '#6b7280',
                                fontWeight: isActive ? '600' : '400'
                            }}>
                                {title}
                            </h6>
                            <h4 className="fw-bold mb-0" style={{
                                color: isActive ? color : '#1f2937',
                                fontSize: "1.5rem"
                            }}>
                                {value}
                            </h4>
                            {subtitle && (
                                <small style={{
                                    fontSize: "0.75rem",
                                    color: isActive ? color : '#6b7280'
                                }}>
                                    {subtitle}
                                </small>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="container-fluid py-2">
            <ToastContainer position="top-right" autoClose={1500} theme="light" />

            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 mb-2">
                                <p className="fw-bold mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                                    Manage staff accounts, roles, and permissions
                                </p>
                                {activeFilter && (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="px-2 py-1 d-flex align-items-center gap-1">
                                            <span className="text-nowrap">
                                                Filter: {activeFilter.type === 'status'
                                                    ? `Status: ${activeFilter.value}`
                                                    : activeFilter.type === 'role'
                                                        ? `Role: ${activeFilter.value}`
                                                        : activeFilter.type}
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 text-white lh-1 fs-6"
                                                onClick={clearFilters}
                                            >
                                                <FiX size={12} />
                                            </Button>
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-primary"
                                        style={{ backgroundColor: '#dbeafe' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#3b82f6' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#1e40af' }}>
                                            {userStats.activeUsers} Active
                                        </span>
                                    </div>
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-secondary"
                                        style={{ backgroundColor: '#f3f4f6' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#6b7280' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#4b5563' }}>
                                            {userStats.inactiveUsers} Inactive
                                        </span>
                                    </div>
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-success"
                                        style={{ backgroundColor: '#dcfce7' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#166534' }}>
                                            {userStats.totalUsers} Total
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={fetchUsers}
                                    disabled={loading}
                                >
                                    <FiRefreshCw size={14} className={loading ? "spinning" : ""} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </Button>

                                {userRole === "superadmin" && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border-0"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={() => setShowModal(true)}
                                    >
                                        <FiUserPlus size={14} />
                                        <span className="d-none d-sm-inline">Add User</span>
                                    </Button>
                                )}

                                {activeFilter && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={clearFilters}
                                    >
                                        <FiX size={14} />
                                        <span className="d-none d-sm-inline">Clear Filter</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-2">
                <Card.Body className="p-0">
                    <div
                        className="p-3 border-bottom bg-gray-50 d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => setStatsCollapsed(!statsCollapsed)}
                        style={{
                            cursor: 'pointer',
                            backgroundColor: '#d3ebffff'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FiTrendingUp size={14} className="text-primary me-2" />
                            <span className="fw-semibold" style={{ fontSize: 12 }}>
                                User Statistics
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <FiChevronDown
                                size={14}
                                className={`text-muted transition-transform ${statsCollapsed ? '' : 'rotate-180'}`}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transform: statsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                                }}
                            />
                        </div>
                    </div>

                    {!statsCollapsed && (
                        <div className="pt-4">
                            <Row className="g-3">
                                <Col md={3}>
                                    <StatCard
                                        title="Total Users"
                                        value={userStats.totalUsers}
                                        icon={<FiUsers />}
                                        color="#374151"
                                        filterType="all"
                                        isActive={!activeFilter}
                                        onFilter={clearFilters}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Active Users"
                                        value={userStats.activeUsers}
                                        subtitle={`${userStats.inactiveUsers} inactive`}
                                        icon={<FiUserCheck />}
                                        color="#16a34a"
                                        filterType="status"
                                        filterValue="active"
                                        isActive={activeFilter?.type === 'status' && activeFilter?.value === "active"}
                                        onFilter={() => setActiveFilter({ type: 'status', value: "active" })}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Inactive Users"
                                        value={userStats.inactiveUsers}
                                        subtitle={`${userStats.activeUsers} active`}
                                        icon={<FiUserX />}
                                        color="#6b7280"
                                        filterType="status"
                                        filterValue="inactive"
                                        isActive={activeFilter?.type === 'status' && activeFilter?.value === "inactive"}
                                        onFilter={() => setActiveFilter({ type: 'status', value: "inactive" })}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Admins"
                                        value={userStats.admins}
                                        subtitle={`${userStats.editors} editors`}
                                        icon={<FiShield />}
                                        color="#dc2626"
                                        filterType="role"
                                        filterValue="admin"
                                        isActive={activeFilter?.type === 'role' && activeFilter?.value === "admin"}
                                        onFilter={() => setActiveFilter({ type: 'role', value: "admin" })}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search users by name"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2">
                                    <Badge bg="success" className="px-2 py-1">
                                        {filteredUsers.filter(u => u.status === "active").length} Active
                                    </Badge>
                                    <Badge bg="secondary" className="px-2 py-1">
                                        {filteredUsers.filter(u => u.status === "inactive").length} Inactive
                                    </Badge>
                                    {activeFilter && (
                                        <Badge bg="primary" className="px-2 py-1">
                                            Filter: {activeFilter.type === 'status'
                                                ? `Status: ${activeFilter.value}`
                                                : activeFilter.type === 'role'
                                                    ? `Role: ${activeFilter.value}`
                                                    : activeFilter.type}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" variant="secondary" />
                                <span className="ms-2 text-muted">Loading users...</span>
                            </div>
                        ) : filteredUsers.length > 0 ? (
                            <DataTable
                                keyField="id"
                                columns={columns}
                                data={filteredUsers}
                                progressPending={loading}
                                pagination
                                responsive
                                highlightOnHover
                                striped
                                dense
                                defaultSortFieldId="user_name"
                                defaultSortAsc={true}
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 100px)"
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f9fafb",
                                            fontWeight: "600",
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            color: "#374151",
                                            borderBottom: "1px solid #e5e7eb"
                                        },
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.875rem",
                                            color: "#374151",
                                            padding: '12px'
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <div className="text-center my-5">
                                <p className="text-muted">No users found</p>
                                {userRole === "superadmin" && (
                                    <Button variant="outline-primary" onClick={() => setShowModal(true)}>
                                        <FiUserPlus className="me-1" /> Add User
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Add User Modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
                scrollable
                backdrop="static"
            >
                <Modal.Header className="bg-white border-0 p-4 shadow-sm">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h5 className="mb-0 fw-bold text-dark">Add New User</h5>
                        <Button
                            variant=""
                            size="sm"
                            onClick={() => setShowModal(false)}
                            className="p-1 border-0 bg-transparent rounded-circle"
                        >
                            <FiX size={18} className="text-muted" />
                        </Button>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <Form onSubmit={handleAddUser}>
                        <div className="row g-3 mb-4">
                            <div className="col-12">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-primary ps-3">Basic Information</h6>
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1 text-danger">Username *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.user_name}
                                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                                    size="sm"
                                    className="rounded-2"
                                    required
                                />
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1 text-danger">Password *</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    size="sm"
                                    className="rounded-2"
                                    required
                                />
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-lg-6">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-info ps-3">Role & Status</h6>
                                <Form.Label className="fw-semibold small mb-1">Role</Form.Label>
                                <Dropdown className="w-100">
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        className="w-100 text-start d-flex align-items-center justify-content-between"
                                        size="sm"
                                    >
                                        {getRoleBadge(formData.user_role)}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100">
                                        {roleOptions.map((role) => (
                                            <Dropdown.Item
                                                key={role.value}
                                                onClick={() => setFormData({ ...formData, user_role: role.value })}
                                                active={formData.user_role === role.value}
                                            >
                                                {getRoleBadge(role.value)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div className="col-lg-6">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-success ps-3">Access Control</h6>
                                <Form.Label className="fw-semibold small mb-1">Status</Form.Label>
                                <Dropdown className="w-100 mb-3">
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        className="w-100 text-start d-flex align-items-center justify-content-between"
                                        size="sm"
                                    >
                                        {formData.status === "active" ?
                                            <Badge bg="success">Active</Badge> :
                                            <Badge bg="warning">Inactive</Badge>
                                        }
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100">
                                        {statusOptions.map((status) => (
                                            <Dropdown.Item
                                                key={status.value}
                                                onClick={() => setFormData({ ...formData, status: status.value })}
                                                active={formData.status === status.value}
                                            >
                                                {status.value === "active" ?
                                                    <Badge bg="success">Active</Badge> :
                                                    <Badge bg="warning">Inactive</Badge>
                                                }
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Form.Label className="fw-semibold small mb-1">Access Permission</Form.Label>
                                <Dropdown className="w-100">
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        className="w-100 text-start d-flex align-items-center justify-content-between"
                                        size="sm"
                                    >
                                        {formData.can_access === 1 ?
                                            <Badge bg="success">Enabled</Badge> :
                                            <Badge bg="danger">Disabled</Badge>
                                        }
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100">
                                        {accessOptions.map((access) => (
                                            <Dropdown.Item
                                                key={access.value}
                                                onClick={() => setFormData({ ...formData, can_access: access.value })}
                                                active={formData.can_access === access.value}
                                            >
                                                {access.value === 1 ?
                                                    <Badge bg="success">Enabled</Badge> :
                                                    <Badge bg="danger">Disabled</Badge>
                                                }
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>

                <Modal.Footer className="bg-white border-0 p-4 shadow-sm">
                    <div className="d-flex justify-content-end w-100 gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)} className="px-4 rounded-2">
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddUser}
                            className="px-4 rounded-2"
                        >
                            Add User
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Edit User Modal */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                size="lg"
                centered
                scrollable
                backdrop="static"
            >
                <Modal.Header className="bg-white border-0 p-4 shadow-sm">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h5 className="mb-0 fw-bold text-dark">Edit User</h5>
                        <Button
                            variant=""
                            size="sm"
                            onClick={() => setShowEditModal(false)}
                            className="p-1 border-0 bg-transparent rounded-circle"
                        >
                            <FiX size={18} className="text-muted" />
                        </Button>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <Form onSubmit={handleEditSubmit}>
                        <div className="row g-3 mb-4">
                            <div className="col-12">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-primary ps-3">User Information</h6>
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1">Username</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editForm.user_name}
                                    onChange={(e) => setEditForm({ ...editForm, user_name: e.target.value })}
                                    size="sm"
                                    className="rounded-2"
                                />
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1">New Password (Optional)</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={editForm.password}
                                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                    size="sm"
                                    className="rounded-2"
                                    placeholder="Leave blank to keep current"
                                />
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1">Role</Form.Label>
                                <Dropdown className="w-100">
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        className="w-100 text-start d-flex align-items-center justify-content-between"
                                        size="sm"
                                    >
                                        {getRoleBadge(editForm.user_role)}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100">
                                        {roleOptions.map((role) => (
                                            <Dropdown.Item
                                                key={role.value}
                                                onClick={() => setEditForm({ ...editForm, user_role: role.value })}
                                                active={editForm.user_role === role.value}
                                            >
                                                {getRoleBadge(role.value)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1">Status</Form.Label>
                                <Dropdown className="w-100">
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        className="w-100 text-start d-flex align-items-center justify-content-between"
                                        size="sm"
                                    >
                                        {editForm.status === "active" ?
                                            <Badge bg="success">Active</Badge> :
                                            <Badge bg="warning">Inactive</Badge>
                                        }
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100">
                                        {statusOptions.map((status) => (
                                            <Dropdown.Item
                                                key={status.value}
                                                onClick={() => setEditForm({ ...editForm, status: status.value })}
                                                active={editForm.status === status.value}
                                            >
                                                {status.value === "active" ?
                                                    <Badge bg="success">Active</Badge> :
                                                    <Badge bg="warning">Inactive</Badge>
                                                }
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>

                        <div className="mb-4">
                            <Form.Label className="fw-semibold small mb-1">Access Permission</Form.Label>
                            <Dropdown className="w-100">
                                <Dropdown.Toggle
                                    variant="outline-secondary"
                                    className="w-100 text-start d-flex align-items-center justify-content-between"
                                    size="sm"
                                >
                                    {editForm.can_access === 1 ?
                                        <Badge bg="success">Enabled</Badge> :
                                        <Badge bg="danger">Disabled</Badge>
                                    }
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="w-100">
                                    {accessOptions.map((access) => (
                                        <Dropdown.Item
                                            key={access.value}
                                            onClick={() => setEditForm({ ...editForm, can_access: access.value })}
                                            active={editForm.can_access === access.value}
                                        >
                                            {access.value === 1 ?
                                                <Badge bg="success">Enabled</Badge> :
                                                <Badge bg="danger">Disabled</Badge>
                                            }
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </Form>
                </Modal.Body>

                <Modal.Footer className="bg-white border-0 p-4 shadow-sm">
                    <div className="d-flex justify-content-end w-100 gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => setShowEditModal(false)} className="px-4 rounded-2">
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleEditSubmit}
                            className="px-4 rounded-2"
                        >
                            Update User
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Users;